// Shared coordinate-range validation for manual addresses. A hand-entered latitude/longitude
// (unlike a Google pick) can be typo'd, out of range, or swapped — so every form must reject
// an invalid pair BEFORE submit rather than letting the backend fail with a generic error.
// Latitude ∈ [-90, 90], longitude ∈ [-180, 180], and both must be finite numbers.

export const LATITUDE_MIN = -90;
export const LATITUDE_MAX = 90;
export const LONGITUDE_MIN = -180;
export const LONGITUDE_MAX = 180;

// Coordinates arrive as numbers (from Google) or strings (manual entry). Coerce generically;
// an empty/blank/non-numeric value yields NaN so range checks below fail closed.
const toNumber = (v: unknown): number => {
  if (typeof v === "number") return v;
  if (typeof v === "string" && v.trim() !== "") return Number(v);
  return NaN;
};

export const latitudeInRange = (v: unknown): boolean => {
  const n = toNumber(v);
  return Number.isFinite(n) && n >= LATITUDE_MIN && n <= LATITUDE_MAX;
};

export const longitudeInRange = (v: unknown): boolean => {
  const n = toNumber(v);
  return Number.isFinite(n) && n >= LONGITUDE_MIN && n <= LONGITUDE_MAX;
};

/**
 * Clamp a coordinate into its valid range.
 *
 * The map's viewport fetch does not send Google's bounds verbatim — it pads them by
 * PAD_RATIO (and the initial load fetches a flat ±10° box around the centre). Neither is
 * clamped by construction, so a zoomed-out or high-latitude viewport produces a padded edge
 * beyond ±180 / ±90, and the backend rejects the whole request:
 *
 *   "The address longitude min must be between -180 and 180."
 *
 * Nothing on screen explains it — the map just stops loading households. Clamping is the
 * right answer rather than dropping the parameter: the padded edge only ever means "as far
 * as the world goes in this direction".
 */
export const clampLatitude = (v: number): number =>
  Math.min(LATITUDE_MAX, Math.max(LATITUDE_MIN, v));

export const clampLongitude = (v: number): number =>
  Math.min(LONGITUDE_MAX, Math.max(LONGITUDE_MIN, v));

export interface CoordinateBox {
  latMax: number;
  latMin: number;
  lngMax: number;
  lngMin: number;
}

/** Clamp every edge of a bounding box into valid latitude/longitude ranges. */
export const clampBox = (box: CoordinateBox): CoordinateBox => {
  let lngMin = clampLongitude(box.lngMin);
  let lngMax = clampLongitude(box.lngMax);
  // A view zoomed out far enough wraps around the globe: its west edge comes back EAST
  // of its east edge (e.g. 138.9 → 60.9). The server reads that as "≥ 138.9 AND ≤ 60.9"
  // and returns NO contacts, so the map went empty (owner, 2026-10-12). A view that wide
  // covers every longitude anyway — ask for all of them.
  if (lngMin > lngMax) {
    lngMin = LONGITUDE_MIN;
    lngMax = LONGITUDE_MAX;
  }
  return {
    latMax: clampLatitude(box.latMax),
    latMin: clampLatitude(box.latMin),
    lngMax,
    lngMin,
  };
};
